/**
 * riskCompute.ts — client-side risk price calculators.
 *
 * Mirror the backend math from:
 *   libs/shared/shared/services/sl_placement_service.py  (computeSlTrigger)
 *   libs/shared/shared/services/target_profit.py          (computeTpPrice)
 *
 * Used by the expanded panel in StrategyPositionsModalV2 to display the
 * *configured* SL trigger / TP target before the broker order is placed
 * (i.e. when slTriggerPrice / exitPrice are still null on the position row).
 *
 * These are pure functions with zero side-effects so they are easily testable
 * with golden values from the backend Python test fixtures.
 */

export type RiskSide = 'BUY' | 'SELL';
export type SlConfigType = 'POINTS' | 'PERCENTAGE' | 'RANGE';
export type TpConfigType = 'POINTS' | 'PERCENTAGE';

export interface SlConfig {
  type: SlConfigType;
  value: number;
  /** Only used when type === 'RANGE'. High and low of the ORB range. */
  rangeHigh?: number;
  rangeLow?: number;
}

export interface TpConfig {
  type: TpConfigType;
  value: number;
}

/**
 * Compute the SL trigger price from the expected entry price, side, and SL config.
 *
 * BUY  legs: SL triggers BELOW entry → entry − offset
 * SELL legs: SL triggers ABOVE entry → entry + offset
 *
 * RANGE type: trigger = rangeLow (BUY) or rangeHigh (SELL). `value` is ignored.
 *
 * Returns null when inputs are invalid (missing prices, unknown type).
 */
export function computeSlTrigger(
  expectedEntryPrice: number | null | undefined,
  side: RiskSide | string,
  cfg: SlConfig | null | undefined,
): number | null {
  if (expectedEntryPrice == null || !cfg) return null;
  const price = expectedEntryPrice;
  const isBuy = side === 'BUY';

  switch (cfg.type) {
    case 'POINTS':
      return isBuy ? price - cfg.value : price + cfg.value;

    case 'PERCENTAGE': {
      const offset = price * (cfg.value / 100);
      return isBuy ? price - offset : price + offset;
    }

    case 'RANGE':
      if (cfg.rangeHigh == null || cfg.rangeLow == null) return null;
      return isBuy ? cfg.rangeLow : cfg.rangeHigh;

    default:
      return null;
  }
}

/**
 * Compute the TP target price from the expected entry price, side, and TP config.
 *
 * BUY  legs: profit ABOVE entry → entry + offset
 * SELL legs: profit BELOW entry → entry − offset
 *
 * Returns null when inputs are invalid.
 */
export function computeTpPrice(
  expectedEntryPrice: number | null | undefined,
  side: RiskSide | string,
  cfg: TpConfig | null | undefined,
): number | null {
  if (expectedEntryPrice == null || !cfg) return null;
  const price = expectedEntryPrice;
  const isBuy = side === 'BUY';

  switch (cfg.type) {
    case 'POINTS':
      return isBuy ? price + cfg.value : price - cfg.value;

    case 'PERCENTAGE': {
      const offset = price * (cfg.value / 100);
      return isBuy ? price + offset : price - offset;
    }

    default:
      return null;
  }
}
